<template>
  <div class="column full-height">
    <header
      class="row items-center justify-between q-px-2x-md full-width no-wrap"
      :style="{ height: '76px' }"
    >
      <span
        class="text-lg text-neutral text-weight-medium"
        :style="{ maxWidth: '75%' }"
      >
        {{ formHeading }}
      </span>

      <PBtn color="primary" flat round @click="onClose">
        <CloseIcon class="icon" />
      </PBtn>
    </header>

    <q-separator color="neutral-40" />

    <div class="q-px-2x-md q-py-xl col overflow-auto full-width">
      <div v-if="props.log" class="row q-col-gutter-xs">
        <div class="col-12 text-sm text-neutral-80">Fingerprint</div>
        <div class="col-12 text-sm text-semibold text-capitalize">
          {{ props.log.finger_print_description }}
        </div>
        <div class="col-12 text-sm text-neutral-80">IP Address</div>
        <div class="col-12 text-sm text-semibold">
          {{ props.log.request_meta_data?.ip_addr || "-" }}
        </div>
        <div class="col-12 text-neutral-80">What Changed</div>
        <div class="col-12 text-semibold">
          <PTable
            :rows="changes"
            :columns="columns"
            :pagination="pagination"
            :hide-pagination="true"
            :rows-per-page-options="[]"
            class="text-xs"
            flat
            dense
            wrap-cells
          >
            <template #body-cell-previous_data="cellProps">
              <q-td :props="cellProps">
                <div class="text-no-wrap" v-html="cellProps.value" />
              </q-td>
            </template>

            <template #body-cell-updated_data="cellProps">
              <q-td :props="cellProps">
                <div class="text-no-wrap" v-html="cellProps.value" />
              </q-td>
            </template>
          </PTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { parseISO } from "date-fns";
import { date, is } from "quasar";
import diff from "microdiff";

import CloseIcon from "assets/icons/close.svg";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const { toggleAuditLogDetailsDrawer } = useAuditLogsStore();

const { formatDate } = date;

const props = defineProps({
  log: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const onClose = () => {
  toggleAuditLogDetailsDrawer({
    open: false,
    level: 0,
  });
};

const formHeading = computed(() => {
  return "Change Details";
});

const ignoredFields = ["__v", "__file_uploaded"];

const formatObject = (change, indent) => {
  return Object.entries(change)
    .filter(([key]) => !ignoredFields.includes(key))
    .map(([key, value], index) => {
      let changeString = "";

      if (indent) {
        changeString += `${index == 0 ? "<br>" : ""}${"&nbsp;".repeat(indent * 2)}`;
      }

      if (Array.isArray(value) && value.length) {
        changeString += `${useChangeCase(key, "capitalCase").value} [${value.length}]
          ${formatChange(value, indent + 1)}`;
      } else {
        changeString += `${useChangeCase(key, "capitalCase").value}: ${formatChange(value, indent + 1)}`;
      }

      return changeString;
    })
    .join("<br>");
};

const formatObjectTypeChange = (change, indent) => {
  if (change === null) {
    return "-";
  } else if (Array.isArray(change)) {
    let delimiter = ", ";

    if (typeof change?.[0] === "object") {
      delimiter = indent ? "<br>" : "<br><br>";
    }

    change = change?.map((value) => {
      return formatChange(value, indent);
    });

    return change.join(delimiter);
  } else if (is.object(change)) {
    return formatObject(change, indent);
  }
};

const formatChange = (change, indent = 0) => {
  switch (typeof change) {
    case "boolean":
      return change ? "Yes" : "No";

    case "number":
      return change;

    case "object":
      return formatObjectTypeChange(change, indent);

    case "string":
      if (parseISO(change).toString() !== "Invalid Date") {
        return formatDate(new Date(change), "DD MMM YYYY hh:mm A");
      }

      return change || "-";

    case "undefined":
      return "-";

    default:
      return change || "-";
  }
};

const columns = [
  {
    name: "field_name",
    label: "Field Name",
    align: "left",
    field: "path",
  },
  {
    name: "previous_data",
    label: "Previous Data",
    align: "left",
    field: "oldValue",
    format: (previousData) => {
      return formatChange(previousData);
    },
  },
  {
    name: "updated_data",
    label: "Updated Data",
    align: "left",
    field: "value",
    format: (updatedData) => {
      return formatChange(updatedData);
    },
  },
];

const getChanges = () => {
  try {
    const previousState = props.log.previous_state || {};
    const currentState = props.log.current_state;

    let diffState = diff(previousState, currentState);

    const seenPaths = [];
    const deDuplicatedDiffState = diffState.filter(({ path }) => {
      if (seenPaths.includes(path[0])) {
        return false;
      }

      seenPaths.push(path[0]);

      return true;
    });

    const changedState = deDuplicatedDiffState
      .filter(({ path }) => {
        return !ignoredFields.includes(path[0]);
      })
      .map((change) => {
        let path = change.path[0];

        let oldValue = previousState[path];
        let value = currentState[path];

        path = useChangeCase(path, "capitalCase").value;

        return {
          path,
          value,
          oldValue,
        };
      });

    return changedState;
  } catch (error) {
    console.error("Error in getChanges", error);

    return [];
  }
};

const changes = computed(getChanges);

const pagination = computed(() => {
  return {
    rowsPerPage: unref(changes).length + 1,
  };
});
</script>
<style lang="scss" scoped>
:deep(th) {
  white-space: nowrap;
}

:deep(td) {
  vertical-align: top;
}
</style>
